/**
 * Relances par e-mail (Brevo) et SMS sans réponse — la partie pure, testée.
 *
 * Romain (26/10/2026) : « un devis sur deux n'arrive jamais dans la boîte du
 * client ». Les devis partaient d'une adresse Gmail, PDF joint : pour beaucoup
 * de messageries, c'est l'allure d'un courrier indésirable. Les relances
 * partent désormais par Brevo, depuis contact@imptoiture.com (domaine
 * authentifié), et le CRM sait si chaque e-mail est délivré, ouvert ou rejeté.
 *
 * Les fonctions Edge (Deno) ne peuvent pas importer ce paquet : elles en
 * recopient les quelques lignes utiles. Ce fichier fait foi, et ses tests
 * aussi.
 */

export interface EmailSettings {
  /** Interrupteur maître des e-mails automatiques, distinct de celui des SMS. */
  actif: boolean;
  /** Jours depuis l'envoi du devis. */
  paliers: number[];
  objet: string;
  message: string;
}

export const EMAIL_SETTINGS_DEFAULT: EmailSettings = {
  actif: false,
  paliers: [7, 14],
  objet: 'Votre devis {numero} — IMP TOITURE',
  message: [
    'Bonjour {prenom},',
    '',
    'Nous vous avons adressé le {date} notre devis n° {numero}, d’un montant de {montant} € TTC.',
    '',
    'Avez-vous pu en prendre connaissance ? Il arrive que nos e-mails se perdent dans les courriers indésirables : si vous ne l’avez pas reçu, répondez simplement à ce message et nous vous le renverrons aussitôt.',
    '',
    'Nous restons à votre disposition pour toute question, ou pour repasser vous voir.',
    '',
    'Bien cordialement,',
    'IMP TOITURE — Couverture, charpente, zinguerie, isolation',
    '04 92 77 14 78 · www.imptoiture.com',
  ].join('\n'),
};

/** Remplace {prenom}, {numero}… ; une variable inconnue devient vide. */
export function remplirModele(modele: string, vars: Readonly<Record<string, string>>): string {
  return modele.replace(/\{(\w+)\}/g, (_, k: string) => vars[k] ?? '');
}

/** Le palier à envoyer aujourd'hui : le plus haut atteint, ou `null`. */
export function palierAtteint(joursDepuisEnvoi: number, paliers: readonly number[]): number | null {
  if (!Number.isFinite(joursDepuisEnvoi)) return null;
  const atteints = paliers.filter((p) => joursDepuisEnvoi >= p).sort((a, b) => a - b);
  return atteints[atteints.length - 1] ?? null;
}

/** Un message texte en HTML simple : paragraphes, retours à la ligne, rien d'injectable. */
export function texteEnHtml(texte: string): string {
  const echapper = (s: string): string =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  return texte
    .split(/\n{2,}/)
    .map((p) => `<p>${echapper(p).replace(/\n/g, '<br>')}</p>`)
    .join('\n');
}

// ----- Ce que Brevo dit d'un e-mail ---------------------------------------------

export type StatutEmail =
  | 'en_file'
  | 'envoye'
  | 'delivre'
  | 'ouvert'
  | 'clique'
  | 'differe'
  | 'rejete'
  | 'spam'
  | 'echec';

export const LIBELLES_STATUT_EMAIL: Record<StatutEmail, string> = {
  en_file: 'En file',
  envoye: 'Envoyé',
  delivre: 'Délivré',
  ouvert: 'Ouvert',
  clique: 'Lien cliqué',
  differe: 'Retardé',
  rejete: 'Rejeté',
  spam: 'Signalé spam',
  echec: 'Échec',
};

/** L'événement d'un webhook Brevo, traduit ; `null` s'il ne change rien. */
export function statutDepuisEvenement(evenement: string): StatutEmail | null {
  switch (evenement) {
    case 'request':
    case 'sent':
      return 'envoye';
    case 'delivered':
      return 'delivre';
    case 'opened':
    case 'unique_opened':
    case 'proxy_open':
      return 'ouvert';
    case 'click':
      return 'clique';
    case 'deferred':
    case 'soft_bounce':
      return 'differe';
    case 'hard_bounce':
    case 'invalid_email':
    case 'blocked':
    case 'error':
      return 'rejete';
    case 'spam':
    case 'complaint':
      return 'spam';
    default:
      return null;
  }
}

const RANG: Record<StatutEmail, number> = {
  en_file: 0,
  envoye: 1,
  differe: 2,
  delivre: 3,
  ouvert: 4,
  clique: 5,
  // Les fins malheureuses l'emportent : un e-mail rejeté ne redevient pas « envoyé ».
  rejete: 10,
  spam: 10,
  echec: 10,
};

/**
 * Le statut après un événement. Les événements arrivent dans le désordre (un
 * « délivré » après un « ouvert ») : on n'avance jamais à reculons.
 */
export function statutEmailSuivant(actuel: StatutEmail, evenement: string): StatutEmail {
  const nouveau = statutDepuisEvenement(evenement);
  if (nouveau === null) return actuel;
  return RANG[nouveau] >= RANG[actuel] ? nouveau : actuel;
}

// ----- SMS : ne rien promettre que l'opérateur ne tient pas ---------------------

/**
 * Le message promet-il une réponse par SMS ? Brevo n'en reçoit aucune : « STOP
 * pour vous désinscrire » ne désinscrirait personne, et « Répondez à ce SMS »
 * resterait lettre morte. Avec Brevo, ces messages sont refusés ou remplacés.
 */
export function prometUneReponseSms(texte: string): boolean {
  return /r[ée]pond(?:ez|re)\s+(?:à|a)\s+ce\s+sms|\bstop\s+(?:pour|au)\b/i.test(texte);
}

/** Les messages types quand l'opérateur ne reçoit pas les réponses (Brevo). */
export const MODELES_SMS_SANS_REPONSE = {
  devis:
    'Bonjour {prenom}, IMP TOITURE. Avez-vous pu étudier notre devis {numero} ({montant} € TTC) ? Nous restons à votre disposition au 04 92 77 14 78.',
  facture:
    'Bonjour {prenom}, IMP TOITURE. Votre facture {numero} de {montant} € est arrivée à échéance. Merci de régulariser. Une question : 04 92 77 14 78.',
  rdv: 'Bonjour {prenom}, IMP TOITURE. Rappel : intervention prévue demain ({date}) à {ville}. En cas d’empêchement : 04 92 77 14 78.',
  avis: 'Bonjour {prenom}, merci d’avoir fait confiance à IMP TOITURE ! Votre avis Google nous aiderait beaucoup : {lien}',
} as const;
